import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AiPresetItem } from '../../data/models/ai-presets.models';
import { AiPresetsService } from '../../data/services/ai-presets.service';
import { PresetCategoriesService } from '../../../preset-categories/data/services/preset-categories.service';
import { PresetCategoryListItem } from '../../../preset-categories/data/models/preset-categories.models';
import { GlobalAlertService } from '../../../../core/services/global-alert.service';

@Component({
  selector: 'app-ai-presets',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-presets.html',
  styleUrl: './ai-presets.scss',
})
export class AiPresets implements OnInit {
  private readonly aiPresetsService = inject(AiPresetsService);
  private readonly presetCategoriesService = inject(PresetCategoriesService);
  private readonly globalAlertService = inject(GlobalAlertService);

  presets: AiPresetItem[] = [];
  totalCount = 0;
  pageNumber = 1;
  pageSize = 20;
  totalPages = 0;

  isLoading = false;
  errorMessage = '';

  deletingPresetId: string | null = null;
  deletingPreset: AiPresetItem | null = null;
  
  isDeleteModalOpen = signal(false);
  
  isUploadModalOpen = signal(false);
  uploadingPresetName = '';
  uploadingPresetDescription = '';
  uploadingPresetCategoryId: number | null = null;
  uploadingPresetFile: File | null = null;
  isUploading = false;

  activeCategories: PresetCategoryListItem[] = [];
  updatingPresetId: number | null = null;

  ngOnInit(): void {
    this.loadPresets();
    this.loadActiveCategories();
  }

  loadActiveCategories(): void {
    this.presetCategoriesService.getCategories(1, 200, undefined, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.activeCategories = response.data.categories || [];
        }
      },
      error: (err) => {
        console.error('Failed to load active preset categories:', err);
      }
    });
  }

  loadPresets(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const offset = (this.pageNumber - 1) * this.pageSize;

    this.aiPresetsService.getPresets(offset, this.pageSize).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.presets = response.data.presets || [];
          this.totalCount = response.data.total || 0;
          this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        } else {
          this.errorMessage = response.message || 'Failed to load AI Presets.';
          this.presets = [];
        }
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load AI Presets.';
        this.presets = [];
        this.isLoading = false;
      },
    });
  }

  nextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.loadPresets();
    }
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadPresets();
    }
  }

  onUploadNewPreset(): void {
    this.isUploadModalOpen.set(true);
    this.uploadingPresetName = '';
    this.uploadingPresetDescription = '';
    this.uploadingPresetCategoryId = null;
    this.uploadingPresetFile = null;
  }

  closeUploadModal(): void {
    this.isUploadModalOpen.set(false);
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.uploadingPresetFile = target.files[0];
    } else {
      this.uploadingPresetFile = null;
    }
  }

  confirmUploadPreset(): void {
    if (!this.uploadingPresetName || !this.uploadingPresetFile || !this.uploadingPresetCategoryId) return;

    this.isUploading = true;
    this.aiPresetsService.uploadPreset(this.uploadingPresetName, this.uploadingPresetDescription, this.uploadingPresetFile, this.uploadingPresetCategoryId).subscribe({
      next: (res) => {
        if (res && res.success !== false) {
          this.loadPresets();
          this.closeUploadModal();
        } else {
          this.errorMessage = res?.message || 'Failed to upload preset.';
        }
        this.isUploading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to upload preset.';
        this.isUploading = false;
      }
    });
  }

  openDeleteModal(preset: AiPresetItem): void {
    this.deletingPreset = preset;
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen.set(false);
    this.deletingPreset = null;
  }

  confirmDeletePreset(): void {
    if (!this.deletingPreset || !this.deletingPreset.perfectlyClearPresetId) return;

    this.deletingPresetId = this.deletingPreset.perfectlyClearPresetId;

    this.aiPresetsService.deletePreset(this.deletingPresetId).subscribe({
      next: (res) => {
        if (res && res.success !== false) {
          if (this.presets.length === 1 && this.pageNumber > 1) {
            this.pageNumber--;
          }
          this.loadPresets();
        } else {
          this.errorMessage = res?.message || 'Failed to delete preset.';
        }
        this.closeDeleteModal();
        this.deletingPresetId = null;
      },
      error: () => {
        this.errorMessage = 'Failed to delete preset.';
        this.closeDeleteModal();
        this.deletingPresetId = null;
      },
    });
  }

  onDeletePreset(preset: AiPresetItem): void {
    this.openDeleteModal(preset);
  }

  onCategoryChange(preset: AiPresetItem, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const newCategoryId = Number(select.value);

    if (!newCategoryId) {
      this.globalAlertService.showError('No category is selected.', 'Invalid Selection');
      select.value = preset.presetCategoryId.toString();
      return;
    }

    if (newCategoryId === preset.presetCategoryId) {
      return; // Unchanged
    }

    if (this.updatingPresetId !== null) {
      select.value = preset.presetCategoryId.toString();
      return;
    }

    this.updatingPresetId = preset.presetId;

    this.aiPresetsService.updatePresetCategory(preset.presetId, { presetCategoryId: newCategoryId }).subscribe({
      next: (response) => {
        this.updatingPresetId = null;
        if (response.success && response.data) {
          this.globalAlertService.showSuccess(response.message || 'Preset category updated successfully.');
          
          // Update the preset row using the response data
          const updatedPreset = response.data;
          const index = this.presets.findIndex((p) => p.presetId === preset.presetId);
          if (index > -1) {
            this.presets[index] = {
              ...this.presets[index],
              presetCategoryId: updatedPreset.presetCategoryId,
              presetCategoryName: updatedPreset.presetCategoryName,
              updatedAt: updatedPreset.updatedAt
            };
          }
          
          this.loadPresets(); // Refresh the list
        } else {
          this.globalAlertService.showError(response.message || 'Failed to update category.');
          select.value = preset.presetCategoryId.toString();
        }
      },
      error: (err) => {
        this.updatingPresetId = null;
        this.globalAlertService.showError(err?.error?.message || 'Failed to update category.');
        select.value = preset.presetCategoryId.toString();
      }
    });
  }

  clearError(): void {
    this.errorMessage = '';
  }
}
